import React, { useState, useCallback, useEffect, useRef } from 'react';
import { priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import { CustomLoadingIcon } from '../../supplementary_components/loading_icon';
import styles from './dashboard.module.scss';
import { sendRequest, API_ROOT } from '../../supplementary_components/requests';
import { PopupOnClick } from '../../general_components/content_management/reusableItems/itemOnPopup';

const fetchDashboardData = (setTrucksData, setLoadsData, userCanSeeLoadRates) => {
  sendRequest('GET', `${API_ROOT}trucks/truck-data-for-dashboard`, null, (data) => setTrucksData(data), () => {});
  if (userCanSeeLoadRates) {
    sendRequest('GET', `${API_ROOT}loads/load-data-for-dashboard`, null, (data) => setLoadsData(data), () => {});
  }
};

export const Dashboard = ({userCanSeeLoadRates}) => {
  const [trucksData, setTrucksData] = useState(null);
  const [loadsData, setLoadsData] = useState(null);

  useEffect(() => {
    fetchDashboardData(setTrucksData, setLoadsData, userCanSeeLoadRates);
  }, [userCanSeeLoadRates]);

  const fleetLoading = trucksData == null;
  const loadsLoading = loadsData == null;

  const activePct = trucksData ? Math.round((trucksData.activeTrucks / trucksData.totalTrucks) * 100) : 0;
  const inactivePct = trucksData ? Math.round((trucksData.inactiveTrucks / trucksData.totalTrucks) * 100) : 0;
  const onRoadPct = trucksData ? Math.round((trucksData.onTheRoadTrucks / trucksData.activeTrucks) * 100) : 0;
  const homePct = 100 - onRoadPct;
  const companyPct = trucksData?.activeTrucks > 0
    ? Math.round((trucksData.activeCompanyTrucks / trucksData.activeTrucks) * 100)
    : 0;
  const leasedPct = trucksData?.activeTrucks > 0
    ? Math.round((trucksData.activeLeasedTrucks / trucksData.activeTrucks) * 100)
    : 0;
  const ownerPct = trucksData?.activeTrucks > 0
    ? Math.round((trucksData.activeOwnerOperatedTrucks / trucksData.activeTrucks) * 100)
    : 0;

  const prevGross = loadsData?.totalWeeklyGrossForPreviousWeekForSamePeriod;
  const currGross = loadsData?.totalWeeklyGross;
  const grossChangePct = prevGross > 0 && currGross != null
    ? ((currGross - prevGross) / prevGross) * 100
    : (currGross > 0 ? 100 : 0);
  const grossBadge = grossChangePct >= 0 ? `+${grossChangePct.toFixed(2)}%` : `${grossChangePct.toFixed(2)}%`;
  const grossAlertBadge = grossChangePct < 0;
  const DAYS_FULL = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
  const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const getWeeklyLabels = (firstDayOfWeek) => {
    if (!firstDayOfWeek) return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const first = String(firstDayOfWeek).toUpperCase();
    const idx = DAYS_FULL.indexOf(first);
    if (idx === -1) return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return [...Array(7)].map((_, i) => DAYS_SHORT[(idx + i) % 7]);
  };
  const weeklyLabels = loadsData?.firstDayOfAWeek
    ? getWeeklyLabels(loadsData.firstDayOfAWeek)
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const thisWeekData = loadsData?.weeklyGrossThisWeek;
  const lastWeekData = loadsData?.weeklyGrossLastWeek;

  return (
    <div className={styles.dashboard}>
      <div className={`${styles.twoCol}${userCanSeeLoadRates ? '' : ` ${styles.singleCol}`}`}>
        <section className={styles.panel}>
          <h2 className={styles.sectionTitle}>Fleet Overview</h2>
          <div className={styles.metricRow}>
            <StatCard
              label="Active Trucks"
              value={trucksData?.activeTrucks}
              badge={`${activePct}%`}
              highlight
              loading={fleetLoading}
            />
            <StatCard
              label="Inactive Trucks"
              value={trucksData?.inactiveTrucks}
              badge={`${inactivePct}%`}
              badgeGray
              alert={true}
              loading={fleetLoading}
              clarifyingData={trucksData?.inactiveTrucksList}
            />
            <StatCard label="Open Trucks" value={trucksData?.openTrucks} highlight={trucksData?.openTrucks === 0} warning={trucksData?.openTrucks > 0} loading={fleetLoading} />
          </div>
          <div className={styles.chartsRow}>
            <div className={styles.chartBlock}>
              <div className={`${styles.chartTitle} ${styles.centerText}`}>Truck Utilization</div>
              {fleetLoading ? (
                <div className={styles.chartLoading}>
                  <CustomLoadingIcon height="2rem" strokeColor="var(--light-gray)" />
                </div>
              ) : (
                <DonutChart
                  segments={[
                    { value: trucksData.onTheRoadTrucks, color: 'var(--main-color)', label: `${onRoadPct}% On the Road (${trucksData.onTheRoadTrucks} trucks)` },
                    { value: trucksData.activeTrucks - trucksData.onTheRoadTrucks, color: 'var(--extra-light-gray)', label: `${homePct}% Home Time (${trucksData.activeTrucks - trucksData.onTheRoadTrucks} trucks)` },
                  ]}
                />
              )}
            </div>
            <div className={styles.chartBlock}>
              <div className={`${styles.chartTitle} ${styles.centerText}`}>Truck Type (Team vs Solo)</div>
              {fleetLoading ? (
                <div className={styles.chartLoading}>
                  <CustomLoadingIcon height="2rem" strokeColor="var(--light-gray)" />
                </div>
              ) : (
                <DonutChart
                  segments={[
                    { value: trucksData.activeTrucks - trucksData.soloTrucks, color: 'var(--main-color)', label: `${trucksData.activeTrucks - trucksData.soloTrucks} Team Trucks` },
                    { value: trucksData.soloTrucks, color: 'var(--extra-light-gray)', label: `${trucksData.soloTrucks} Solo Trucks` },
                  ]}
                />
              )}
            </div>
          </div>
          <div className={styles.chartBlock}>
            <br />
            <div className={styles.chartTitle}>Active Trucks Distribution</div>
            {fleetLoading ? (
              <div className={styles.chartLoading}>
                <CustomLoadingIcon height="2rem" strokeColor="var(--light-gray)" />
              </div>
            ) : (
              <div className={styles.barChart}>
                <BarRow
                  label="Company Owned"
                  value={trucksData.activeCompanyTrucks}
                  pct={companyPct}
                  color="var(--main-color)"
                />
                <BarRow
                  label="Lease-Trucks"
                  value={trucksData.activeLeasedTrucks}
                  pct={leasedPct}
                  color="var(--main-color)"
                />
                <BarRow
                  label="Owner-Operator"
                  value={trucksData.activeOwnerOperatedTrucks}
                  pct={ownerPct}
                  color="var(--main-color)"
                />
              </div>
            )}
          </div>
        </section>

        {userCanSeeLoadRates ? (
          <section className={styles.panel}>
            <h2 className={styles.sectionTitle}>Load & Financial Overview</h2>
            <div className={styles.metricRow}>
              <StatCard
                label="Ready to Invoice Today"
                value={priceOnFinish(loadsData?.readyToInvoiceLoadAmount, true)}
                subtext={loadsData ? `${loadsData.readyToInvoiceLoadCount} completed loads` : ''}
                highlight
                loading={loadsLoading}
              />
              <StatCard
                label="Potential Additions"
                value={loadsData?.potentialAdditionAmount ? priceOnFinish(loadsData?.potentialAdditionAmount, true) : '-'}
                subtext={loadsData && loadsData.potentialAdditionCount ? `${loadsData.potentialAdditionCount} loads finishing soon` : ''}
                loading={loadsLoading}
                clarifyingData={loadsData?.potentialAdditionLoads}
                clarifyingDataPopupType="load"
              />
            </div>
            <StatCard
              label="This Week's Gross"
              value={priceOnFinish(loadsData?.totalWeeklyGross, true)}
              badge={grossBadge}
              subtext={loadsData ? `${priceOnFinish(loadsData.totalWeeklyGrossForPreviousWeekForSamePeriod, true)} previous period` : ''}
              alertBadge={grossAlertBadge}
              loading={loadsLoading}
            />
            <div className={styles.chartBlock}>
              <br />
              <div className={styles.chartTitle}>Weekly Gross Revenue</div>
              {loadsLoading ? (
                <div className={styles.chartLoading}>
                  <CustomLoadingIcon height="2rem" strokeColor="var(--light-gray)" />
                </div>
              ) : (
                <LineChart thisWeek={thisWeekData} lastWeek={lastWeekData} labels={weeklyLabels} />
              )}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
};


const formatClarifyingEntry = (entry) => {
  if (entry == null) return '—';
  if (typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean') {
    return String(entry);
  }
  if (typeof entry === 'object') {
    if (entry.name != null) return String(entry.name);
    if (entry.label != null) return String(entry.label);
    if (typeof entry.toString === 'function' && entry.toString !== Object.prototype.toString) {
      return String(entry);
    }
    try {
      return JSON.stringify(entry);
    } catch {
      return String(entry);
    }
  }
  return String(entry);
};

const StatCard = ({
  label,
  value,
  subtext,
  badge,
  badgeGray,
  highlight,
  alert,
  warning,
  alertBadge,
  loading,
  clarifyingData,
  clarifyingDataPopupType,
}) => {
  const [clarifyOpen, setClarifyOpen] = useState(false);
  const cardRef = useRef(null);

  const hasClarify = clarifyingData != null;

  useEffect(() => {
    if (!clarifyOpen) return;
    const onDocMouseDown = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        setClarifyOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setClarifyOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [clarifyOpen]);

  const toggleClarify = useCallback((e) => {
    e.stopPropagation();
    setClarifyOpen((v) => !v);
  }, []);

  const renderClarifyBody = () => {
    if (Array.isArray(clarifyingData)) {
      if (clarifyingData.length === 0) {
        return <div className={styles.clarifyPlain}>No entries</div>;
      }
      return (
        <ul className={styles.clarifyList}>
          {clarifyingData.map((entry, i) => (
            <li key={i} className={styles.clarifyListItem}>
              {clarifyingDataPopupType ? <PopupOnClick text={formatClarifyingEntry(entry.label)} itemType={clarifyingDataPopupType} itemId={entry.id} /> : formatClarifyingEntry(entry)}
            </li>
          ))}
        </ul>
      );
    }
    return <div className={styles.clarifyPlain}>{formatClarifyingEntry(clarifyingData)}</div>;
  };

  return (
    <div
      ref={cardRef}
      className={`${styles.card} ${highlight ? styles.cardHighlight : ''} ${hasClarify ? styles.cardHasClarify : ''}`}
    >
      {hasClarify && (
        <div className={styles.clarifyAnchor}>
          <button
            type="button"
            className={`${styles.clarifyTrigger} ${clarifyOpen ? styles.clarifyTriggerOpen : ''}`}
            onClick={toggleClarify}
            aria-label={`Details for ${label}`}
            aria-expanded={clarifyOpen}
            aria-haspopup="true"
            style={{ cursor: 'pointer' }}
          >
            <span className={styles.clarifyTriggerMark} aria-hidden>
              !
            </span>
          </button>
          {clarifyOpen && (
            <div className={styles.clarifyPopover} role="region" aria-label={`${label} details`}>
              {renderClarifyBody()}
            </div>
          )}
        </div>
      )}
      <div className={styles.cardLabel}>{label}</div>
      <div className={styles.cardValueRow}>
        {loading ? (
          <CustomLoadingIcon height="1.5rem" strokeColor="var(--light-gray)" />
        ) : (
          <>
            <span className={`${styles.cardValue} ${alert ? styles.alertValue : warning ? styles.warningValue : ''}`}>{value}</span>
            {badge != null && (
              <span className={`${styles.badge} ${alert || alertBadge ? styles.alertBadge : warning ? styles.warningBadge : badgeGray ? styles.badgeGray : ''} `}>{badge}</span>
            )}
          </>
        )}
      </div>
      {!loading && subtext && <div className={styles.cardSubtext}>{subtext}</div>}
    </div>
  );
};

const DonutChart = ({ segments, size = 140 }) => {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  let acc = 0;
  const conicParts = segments
    .map((seg) => {
      const pct = total > 0 ? (seg.value / total) * 100 : 0;
      const part = `${seg.color} ${acc}% ${acc + pct}%`;
      acc += pct;
      return part;
    })
    .join(', ');
  const holeSize = size * 0.6;
  return (
    <div className={styles.donutWrap}>
      <div className={styles.donutOuter} style={{ width: size, height: size }}>
        <div
          className={styles.donut}
          style={{
            background: `conic-gradient(${conicParts})`,
            width: size,
            height: size,
          }}
        />
        <div
          className={styles.donutHole}
          style={{
            width: holeSize,
            height: holeSize,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
      <div className={styles.donutLegend}>
        {segments.map((seg, i) => (
          <div key={i} className={styles.legendItem}>
            <span className={styles.legendDot} style={{ background: seg.color }} />
            <span>{seg.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const BarRow = ({ label, value, pct, color }) => (
  <div className={styles.barRow}>
    <div className={styles.barLabelRow}>
      <span className={styles.barLabel}>{label}</span>
    </div>
    <div>
    <span className={styles.barValue}>{value}</span>
    </div>
    <div className={styles.barTrack}>
      <div
        className={styles.barFill}
        style={{ width: `${Math.min(pct, 100)}%`, background: color }}
      />
    </div>
    <span className={styles.barPct}>{pct}%</span>
  </div>
);

const formatCurrency = (val) =>
  `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const LineChart = ({ thisWeek, lastWeek, labels }) => {
  const [hoverIndex, setHoverIndex] = useState(null);

  const all = [...thisWeek, ...lastWeek].filter((v) => v != null && !isNaN(v));
  const max = Math.max(...all, 1);
  const min = Math.min(...all, 0);
  const range = max - min || 1;
  const h = 120;
  const w = 900; // viewBox width; larger value = path uses full SVG width when scaled
  const pad = { x: 0, y: 15 };
  const chartW = w - pad.x * 2;
  const chartH = h - pad.y * 2;
  const toY = (v) => pad.y + chartH - ((v - min) / range) * chartH;
  const toX = (i) => pad.x + (i / (labels.length - 1 || 1)) * chartW;

  const handleMouseMove = useCallback(
    (e) => {
      const svg = e.currentTarget;
      const rect = svg.getBoundingClientRect();
      if (rect.width <= 0 || labels.length === 0) return;
      const x = ((e.clientX - rect.left) / rect.width) * w;
      const idx = Math.round(((x - pad.x) / chartW) * (labels.length - 1));
      const clamped = Math.max(0, Math.min(idx, labels.length - 1));
      setHoverIndex(clamped);
    },
    [labels.length, w, pad.x, chartW]
  );

  const handleMouseLeave = useCallback(() => setHoverIndex(null), []);

  const pathThis = thisWeek
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${toX(i)} ${toY(v)}`)
    .join(' ');
  const pathLast = lastWeek
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${toX(i)} ${toY(v)}`)
    .join(' ');

  const vThis = hoverIndex != null && thisWeek[hoverIndex] != null ? thisWeek[hoverIndex] : null;
  const vLast = hoverIndex != null && lastWeek[hoverIndex] != null ? lastWeek[hoverIndex] : null;
  const pctChange =
    vThis != null && vLast != null && vLast > 0
      ? ((vThis - vLast) / vLast) * 100
      : null;

  return (
    <div className={styles.lineChartWrap}>
      <div className={styles.lineChartSvgContainer}>
        <svg
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          className={styles.lineChartSvg}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <path d={pathLast} className={styles.linePathLast} fill="none" strokeWidth="2" />
          <path d={pathThis} className={styles.linePathThis} fill="none" strokeWidth="2" />
          {hoverIndex != null && (
            <>
              <line
                x1={toX(hoverIndex)}
                y1={pad.y}
                x2={toX(hoverIndex)}
                y2={pad.y + chartH}
                strokeDasharray="4 3"
                className={styles.lineChartHoverLine}
              />
              {vThis != null && (
                <circle
                  cx={toX(hoverIndex)}
                  cy={toY(vThis)}
                  r={4}
                  className={styles.lineChartDotThis}
                />
              )}
              {vLast != null && (
                <circle
                  cx={toX(hoverIndex)}
                  cy={toY(vLast)}
                  r={4}
                  className={styles.lineChartDotLast}
                />
              )}
            </>
          )}
          <rect
            x={pad.x}
            y={pad.y}
            width={chartW}
            height={chartH}
            fill="transparent"
            style={{ cursor: 'crosshair' }}
          />
        </svg>
        {hoverIndex != null && vThis != null && vLast != null && (
          <div
            className={styles.lineChartTooltip}
            style={{
              left: `${(toX(hoverIndex) / w) * 100}%`,
              transform: 'translateX(-50%)',
              top: 8,
            }}
          >
            <div className={styles.lineChartTooltipHeader}>
              <span className={styles.lineChartTooltipTitle}>Gross volume</span>
              {pctChange != null && (
                <span
                  className={
                    pctChange >= 0 ? styles.lineChartTooltipPctPos : styles.lineChartTooltipPctNeg
                  }
                >
                  {pctChange >= 0 ? '+' : ''}{pctChange.toFixed(1)}%
                </span>
              )}
            </div>
            <div className={styles.lineChartTooltipRow}>
              <span className={styles.lineChartTooltipSwatch} style={{ background: 'var(--main-color)' }} />
              <span>{labels[hoverIndex]}</span>
              <span className={styles.lineChartTooltipValue}>{formatCurrency(vThis)}</span>
            </div>
            <div className={styles.lineChartTooltipRow}>
              <span className={styles.lineChartTooltipSwatch} style={{ background: 'var(--light-gray)' }} />
              <span>{labels[hoverIndex]}</span>
              <span className={styles.lineChartTooltipValue}>{formatCurrency(vLast)}</span>
            </div>
          </div>
        )}
      </div>
      <div className={styles.lineChartLabels}>
        {labels.map((l, i) => (
          <span key={i} className={styles.lineChartLabel}>
            {l}
          </span>
        ))}
      </div>
      <div className={styles.lineChartLegend}>
        <div className={styles.legendItem}>
          <span className={styles.legendLine} style={{ background: 'var(--main-color)' }} />
          <span className={styles.legendLabel}>This Week</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendLineDashed} />
          <span className={styles.legendLabel}>Last Week</span>
        </div>
      </div>
    </div>
  );
};


